import React, { useContext, useEffect, useRef, useState } from "react";
import { defineMessages, FormattedMessage, injectIntl } from "react-intl";
import PropTypes from "prop-types";
import * as styles from "./InstallStyles.scss";
import { connect } from "react-redux";
import { mapDispatchToProps, mapStateToProps, mergeProps } from "../../containers/SitesPage";
import { getUserId } from "../../redux/selectors/ui/user";
import { Alert, Button, Title } from "@yoast/ui-library";
import SelectOrAddSite from "./SelectOrAddSite";
import NoLicenseModal from "../modal/NoLicenseModal";
import NotCompatibleTYPO3Modal from "../modal/NotCompatibleTYPO3Modal";
import { InstallContext } from "./InstallContext";
import useToggleState from "../../reactHooks/useToggleState";
import { doRequest, prepareInternalRequest } from "shared-frontend/functions/api";
import stripCommonWrongSubdirectories from "../../functions/stripCommonWrongSubdirectories";
import classNames from "classnames";
import ButtonsContainer from "../general/ButtonsContainer";
import { ArrowLongLeftIcon, ArrowLongRightIcon } from "@heroicons/react/20/solid";
import { SkipInstallationFlowLink } from "./SkipInstallationFlowLink";

const messages = defineMessages( {
	title: {
		id: "install.title",
		defaultMessage: "Let's install {plugin} on your site!",
		values: { plugin: "Yoast SEO Premium" },
	},
	formIntroductionTitle: {
		id: "install.formIntroductionTitle",
		defaultMessage: "Provide one site to install {plugin}",
		values: { plugin: "Yoast SEO Premium" },
	},
	formIntroductionDescription: {
		id: "install.formIntroductionDescription",
		defaultMessage: "You can either select an existing site from the dropdown or enter a new site URL below.",
	},
	continueButton: {
		id: "install.button.continue",
		defaultMessage: "Continue",
	},
	urlCommonWrongSubdirectoryWarning: {
		id: "install.urlCommonWrongSubdirectoryWarning",
		defaultMessage: `It appears you have included "{warning}" in your url. This is allowed
						but not necessary. We will save your site as: {link}.`,
	},
	back: {
		id: "install.button.back",
		defaultMessage: "Back",
	},
} );

/**
 * Install a subscription on a site flow.
 *
 * @param {object} props The properties.
 *
 * @returns {ReactComponent} The rendered component.
 */
const ConnectSite = injectIntl( ( props ) => {
	const {
		      invoiceNumberParam,
		      steps,
		      goToNextStep,
		      goToPreviousStep,
		      setConnectedSiteURL,
	      }                                                                                                = useContext( InstallContext );
	const [ selectedSite, setSelectedSite ]                                                                = useState( "" );
	const [ subdirectoryWarning, setSubdirectoryWarning ]                                                  = useState( "" );
	const [ isLoading, , , startLoading, stopLoading ]                                                     = useToggleState( false );
	const [ isNoLicensesModalOpen, , , openNoLicensesModal, closeNoLicensesModal ]                         = useToggleState( false );
	const [ isNotCompatibleTYPO3ModalOpen, , , openNotCompatibleTYPO3Modal, closeNotCompatibleTYPO3Modal ] = useToggleState( false );
	const mounted                                                                                          = useRef( false );

	useEffect( () => {
		mounted.current = true;

		return () => {
			mounted.current = false;
		};
	}, [] );

	/**
	 * Handles the error when linking a site to a subscription.
	 * @param {Object} error The API error object.
	 * @returns {void}
	 */
	function handleLinkSiteError( error ) {
		switch ( error.message ) {
			case "No valid subscriptions found.":
				openNoLicensesModal();
				break;
			case "Site type is not supported.":
				openNotCompatibleTYPO3Modal();
				break;
			default:
			// Do nothing.
		}
	}

	/**
	 * Sends a POST request to link the provided site URL to a subscription.
	 * Sets the linkedSite variable to the provided URL.
	 *
	 * @returns {object} The site with the subscription relation.
	 */
	async function linkSiteToSubscription() {
		if ( isLoading ) {
			return;
		}

		startLoading();

		try {
			await doRequest(
				prepareInternalRequest(
					"Subscriptions/link-site/",
					"POST", {
						url: selectedSite,
						invoiceNumber: invoiceNumberParam,
					},
				),
			);

			setConnectedSiteURL( selectedSite );
			goToNextStep();
		} catch ( error ) {
			if ( ! mounted.current ) {
				return;
			}
			handleLinkSiteError( error );
		} finally {
			if ( mounted.current ) {
				stopLoading();
			}
		}
	}

	/**
	 * Sets url on change of inputfield
	 *
	 * @param {string} url The input url from the client
	 *
	 * @returns {void} The stripped url.
	 */
	function onSiteUrlChange( url ) {
		const strippedUrlResult = stripCommonWrongSubdirectories( url );
		setSubdirectoryWarning( strippedUrlResult.strippedSubdirectory );
		setSelectedSite( strippedUrlResult.url );
	}

	const readyToDownload = Boolean( selectedSite && ! isLoading );

	return (
		<div className={ classNames( styles.connectSiteStep, styles.ySpacing ) }>
			<Title className={ styles.textCenter }><FormattedMessage { ...messages.title } /></Title>
			<hr />
			<div className={ styles.ySpacing }>
				<div className={ styles.ySmallSpacing }>
					<Title size={ "3" } as={ "span" }>
						<FormattedMessage { ...messages.formIntroductionTitle } />
					</Title>
					{ props.sites.length >= 1 &&
						<p><FormattedMessage { ...messages.formIntroductionDescription } /></p> }
				</div>
				<SelectOrAddSite
					existingSites={ props.sites }
					onSiteUrlChange={ onSiteUrlChange }
				/>
				{ subdirectoryWarning &&
					<Alert variant="info">
						<FormattedMessage
							{ ...messages.urlCommonWrongSubdirectoryWarning }
							values={ { link: selectedSite, warning: subdirectoryWarning } }
						/>
					</Alert>
				}
			</div>

			<hr />

			<ButtonsContainer className={ styles.buttonContainer }>
				{ steps.canGoBack && <Button variant={ "tertiary" } onClick={ goToPreviousStep }>
					<ArrowLongLeftIcon className={ styles.buttonIcon } />
					<FormattedMessage { ...messages.back } />
				</Button> }
				<Button
					id="install-addon-download-action"
					className="install-addon-download-button"
					onClick={ linkSiteToSubscription }
					disabled={ ! readyToDownload }
					aria-disabled={ ! readyToDownload }
				>
					<FormattedMessage { ...messages.continueButton } />
					<ArrowLongRightIcon className={ styles.buttonIcon } />
				</Button>
			</ButtonsContainer>
			<SkipInstallationFlowLink />

			{ isNoLicensesModalOpen && <NoLicenseModal closeModal={ closeNoLicensesModal } /> }

			{ isNotCompatibleTYPO3ModalOpen && <NotCompatibleTYPO3Modal closeModal={ closeNotCompatibleTYPO3Modal } /> }
		</div>
	);
} );

ConnectSite.propTypes = {
	sites: PropTypes.array,
	loadingSites: PropTypes.bool,
	submitAddSite: PropTypes.func.isRequired,
	plugins: PropTypes.array,
	customerId: PropTypes.string.isRequired,
};

ConnectSite.defaultProps = {
	sites: [],
	plugins: [],
	loadingSites: false,
};

export default connect(
	( state ) => (
		{
			...mapStateToProps( state ),
			customerId: getUserId( state ),
		}
	),
	mapDispatchToProps,
	mergeProps,
)( ConnectSite );
